import { Button, Input, Select, SelectItem } from '@heroui/react'
import { Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  Button as AriaButton,
  Label,
  Tag,
  TagGroup,
  TagList,
} from 'react-aria-components'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'

const outcomeTypes = [
  { value: 'success', label: 'Success', color: 'success' },
  { value: 'failure', label: 'Failure', color: 'danger' },
  { value: 'neutral', label: 'Neutral', color: 'default' },
]

export default function QCTagsSettings({ org }) {
  const [qcTags, setQCTags] = useState([])
  const [newTag, setNewTag] = useState({ label: '', type: 'neutral' })
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (org?.preferences?.qc_tags) {
      setQCTags(org.preferences.qc_tags)
    }
    else if (org) {
      // Set default outcomes if none exist
      const defaultOutcomes = [
        { label: 'Successful', type: 'success' },
        { label: 'Technical Issue', type: 'failure' },
        { label: 'Wrong Number', type: 'failure' },
        { label: 'No Answer', type: 'failure' },
        { label: 'Needs Review', type: 'neutral' },
      ]
      setQCTags(defaultOutcomes)
    }
  }, [org])

  const handleAddTag = () => {
    if (!newTag.label.trim()) {
      toast.error('Please enter a label for the tag')
      return
    }

    // Check if tag with same label already exists
    if (qcTags.some(tag => tag.label.toLowerCase() === newTag.label.trim().toLowerCase())) {
      toast.error('A tag with this label already exists')
      return
    }

    const updatedTags = [...qcTags, { label: newTag.label.trim(), type: newTag.type }]
    setQCTags(updatedTags)
    setNewTag({ label: '', type: 'neutral' })
  }

  const handleRemoveTag = (label) => {
    const updatedTags = qcTags.filter(tag => tag.label !== label)
    setQCTags(updatedTags)
  }

  const handleSaveTags = async () => {
    try {
      setIsSaving(true)
      await $fetch(`/orgs/${org.id}/properties`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: {
            preferences: {
              ...org.preferences,
              qc_tags: qcTags,
            },
          },
        }),
      })
      toast.success('QC tags updated successfully')
    }
    catch (error) {
      console.error('Error saving QC tags:', error)
      toast.error('Failed to save QC tags')
    }
    finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="mt-8 border-t border-gray-200 pt-8">
      <div className="mb-6">
        <h3 className="text-lg font-semibold">Quality Control Tags</h3>
        <p className="text-sm text-gray-600">
          Define QC tags that can be applied to calls during review.
          Multiple tags can be selected for each call.
        </p>
      </div>

      {/* Existing tags */}
      <div className="mb-6">
        {qcTags.length === 0
          ? (
              <p className="text-sm text-gray-500">No QC tags defined yet.</p>
            )
          : (
              <TagGroup>
                <Label className="mb-2 block text-sm font-medium text-gray-700">Current Tags</Label>
                <TagList className="flex flex-wrap gap-2">
                  {qcTags.map(tag => (
                    <Tag
                      key={tag.label}
                      textValue={tag.label}
                      className={`
                        group flex items-center gap-1.5 rounded-full px-3 py-1.5
                        text-sm font-medium transition-all
                        ${
                    tag.type === 'success'
                      ? 'border border-green-200 bg-green-100 text-green-800'
                      : tag.type === 'failure'
                        ? 'border border-red-200 bg-red-100 text-red-800'
                        : 'border border-gray-200 bg-gray-100 text-gray-800'
                    }
                      `}
                    >
                      <span>{tag.label}</span>
                      <AriaButton
                        slot="remove"
                        className={`
                          rounded-full opacity-60 transition-opacity
                          outline-none
                          hover:opacity-100
                          focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                        `}
                        onPress={() => handleRemoveTag(tag.label)}
                      >
                        <X size={14} />
                      </AriaButton>
                    </Tag>
                  ))}
                </TagList>
              </TagGroup>
            )}
      </div>

      {/* Add new outcome */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <h4 className="mb-3 text-sm font-medium">Add New Tag</h4>
        <div className="flex gap-3">
          <Input
            size="sm"
            placeholder="Tag label (e.g., 'Successful', 'Wrong Number')"
            value={newTag.label}
            onChange={e => setNewTag({ ...newTag, label: e.target.value })}
            className="flex-1"
          />
          <Select
            size="sm"
            selectedKeys={[newTag.type]}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0]
              setNewTag({ ...newTag, type: value })
            }}
            className="w-40"
          >
            {outcomeTypes.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </Select>
          <Button
            size="sm"
            color="primary"
            startContent={<Plus size={16} />}
            onClick={handleAddTag}
          >
            Add
          </Button>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          radius="none"
          color="primary"
          onClick={handleSaveTags}
          isLoading={isSaving}
          isDisabled={isSaving}
        >
          Save QC Tags
        </Button>
      </div>
    </div>
  )
}
